import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useInventory } from '../../contexts/InventoryContext';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiPackage, FiDollarSign, FiBarChart3, FiImage, FiZap } = FiIcons;

const ProductCard = ({ product, onClick }) => {
  const { updateProductImageWithAI } = useInventory();
  const [isUpdatingImage, setIsUpdatingImage] = useState(false);

  const getStockStatus = (quantity) => {
    if (quantity === 0) return { status: 'Out of Stock', color: 'red' };
    if (quantity <= 10) return { status: 'Low Stock', color: 'yellow' };
    return { status: 'In Stock', color: 'green' };
  };

  const stockStatus = getStockStatus(product.quantity);
  
  const getStatusClasses = (color) => {
    if (color === 'green') return 'bg-green-100 text-green-800 dark:bg-green-900/50 dark:text-green-300';
    if (color === 'yellow') return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/50 dark:text-yellow-300';
    return 'bg-red-100 text-red-800 dark:bg-red-900/50 dark:text-red-300';
  };

  const handleSmartImageSearch = async (e) => {
    e.stopPropagation();
    if (isUpdatingImage) return;
    setIsUpdatingImage(true);
    await updateProductImageWithAI(product);
    setIsUpdatingImage(false);
  };

  // Prioritize the first image from the images array, fallback to the legacy image field
  const displayImage = (product.images && product.images.length > 0) 
    ? product.images[0] 
    : product.image;

  return (
    <motion.div
      whileHover={{ scale: 1.02 }}
      whileTap={{ scale: 0.98 }}
      onClick={onClick}
      className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6 cursor-pointer hover:shadow-md dark:hover:shadow-gray-900 transition-all duration-200 group relative"
    >
      {/* Product Image */}
      <div className="mb-4 relative">
        {displayImage ? (
          <div className="aspect-square w-full rounded-lg overflow-hidden bg-gray-100 dark:bg-gray-700 relative">
            <img 
              src={displayImage} 
              alt={product.name} 
              className="w-full h-full object-cover"
              onError={(e) => {
                e.target.style.display = 'none';
                e.target.nextSibling.style.display = 'flex';
              }}
            />
            <div className="w-full h-full hidden items-center justify-center bg-gray-100 dark:bg-gray-700">
              <SafeIcon icon={FiImage} className="h-12 w-12 text-gray-300 dark:text-gray-500" />
            </div>
          </div>
        ) : (
          <div className="aspect-square w-full rounded-lg bg-gray-100 dark:bg-gray-700 flex items-center justify-center">
            <SafeIcon icon={FiImage} className="h-12 w-12 text-gray-300 dark:text-gray-500" />
          </div>
        )}

        {/* Smart Image Search Button */}
        <button
          onClick={handleSmartImageSearch}
          disabled={isUpdatingImage}
          className={`absolute top-2 right-2 p-2 rounded-full shadow-md transition-all duration-200 z-10 ${
            isUpdatingImage 
              ? 'bg-blue-100 text-blue-600 animate-pulse cursor-wait' 
              : 'bg-white/90 dark:bg-gray-800/90 text-gray-500 hover:text-blue-600 dark:text-gray-400 dark:hover:text-blue-400 opacity-0 group-hover:opacity-100 hover:scale-110'
          }`}
          title="Smart Image Search & Update"
        >
          {isUpdatingImage ? (
            <div className="h-4 w-4 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
          ) : (
            <SafeIcon icon={FiZap} className="h-4 w-4" />
          )}
        </button>
      </div>

      <div className="flex items-start justify-between mb-4">
        <div className="flex-1 min-w-0">
          <h3 className="font-semibold text-gray-900 dark:text-white text-lg mb-1 truncate">
            {product.name}
          </h3>
          <p className="text-gray-600 dark:text-gray-400 text-sm truncate">
            {product.manufacturer} - {product.model}
          </p>
        </div>
        <div className="ml-4 text-right flex-shrink-0">
          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusClasses(stockStatus.color)}`}>
            {stockStatus.status}
          </span>
        </div>
      </div>

      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center text-gray-600 dark:text-gray-400">
            <SafeIcon icon={FiPackage} className="h-4 w-4 mr-2" />
            <span className="text-sm">SKU: {product.localSku}</span>
          </div>
        </div>
        <div className="flex items-center justify-between">
          <div className="flex items-center text-gray-600 dark:text-gray-400">
            <SafeIcon icon={FiBarChart3} className="h-4 w-4 mr-2" />
            <span className="text-sm">Quantity: {product.quantity}</span>
          </div>
          <div className="flex items-center text-gray-900 dark:text-white font-semibold">
            <SafeIcon icon={FiDollarSign} className="h-4 w-4 mr-1" />
            <span>${product.localPrice.toFixed(2)}</span>
          </div>
        </div>
        
        {product.salesListings && product.salesListings.length > 0 && (
          <div className="pt-2 border-t border-gray-100 dark:border-gray-700">
            <p className="text-xs text-gray-500 dark:text-gray-400">
              {product.salesListings.length} sales listing{product.salesListings.length !== 1 ? 's' : ''}
            </p>
          </div>
        )}
      </div>
    </motion.div>
  );
};

export default ProductCard;